# Route: /today — the universal, role-agnostic post-login landing (F53b §A).
# Replaces dashboard.cir.yaml AND the operator /home tile page. Composition is
# server-resolved (§A.2): the page's `composition` data source fetches
# GET /api/today/widgets through the canonical data provider (which records the
# §A.8 visit), and the today-widget-grid fragment renders the resolved `blocks`
# from the `composition` param its placement binds (`$data.composition`). A widget the
# user hides on /profile is absent from `blocks` and never renders here — the
# §A.16 round-trip. Visibility is data-driven (not CIR `permission:`-gated) so a
# profile toggle is observable without re-emitting; the greeting stays a CIR
# PageHeader (always). Permission: "public" (every authenticated user, §A.1).

page-today:
  type: Page
  archetype: dashboard
  meta:
    title: $t.today.meta_title
    route: /today
    layout: shell
    auth: required
  data:
    composition:
      model: TodayComposition
      refresh: on_mount
      data_states:
        loading:
          skeleton_count: 3
        error: default
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - today-greeting
    - today-greeting-name
    - today-grid

# ── Greeting (always; §A.8 tone bucket recorded server-side on the data fetch) ─
today-greeting:
  type: PageHeader
  content: $t.today.greeting

# bug-display-name-renders-as-guid.md / M1 Act 4.1 — the operator's resolved
# display name, directly under the greeting title. `composition.greeting`
# carries the same DisplayName GetMyProfileQuery resolves for /profile (never
# the raw user id — see UserProfile.DefaultDisplayName), so this is the single
# canonical name resolution rendered here, not a second one. Plain `$data.path`
# binding renders empty (collapses, no dangling text) if the composition fetch
# hasn't resolved yet — the same dynamic-content pattern already proven on
# system-info's system-version-value; no xp85 changes needed.
today-greeting-name:
  type: Text
  content: $data.composition.greeting.displayName
  tokens:
    font_size: $font.size.lg
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

# ── Composition grid (§A.2) — the one Lit fragment that renders the resolved
# blocks (ai-summary slot, permission+toggle-resolved nav cards, quick-actions).
# Reads the composition from its `composition` param, bound to $data.composition;
# no own fetch — the page data source (canonical provider) owns the request.
# permission: "public" — the server composition, not CIR, decides which blocks
# a user sees. The grid reads its own text from the today.* / today_widget.*
# locale keys, so the placement passes no labels.
today-grid:
  type: Widget
  ref: today-widget-grid
  permission: "public"
  params:
    composition: $data.composition

# Empty state (§A.12) is rendered by the today-widget-grid fragment itself: the
# composition payload always carries the always-visible greeting block, so the
# CIR data_states.empty (whole-payload-empty) branch is unreachable. The grid
# reports the empty case via data-grid-state="empty" once blocks (greeting
# excluded) filter to zero, and renders the canonical empty UI from the
# today.empty / empty_body / empty_cta locale keys.
